import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { fetchSpot, setLastSpotRoute } from '../../store/slices/savedSpotsSlice';
import Header from '../../layout/Header'
import MobileNav from '../../layout/MobileNav'
import PageNavSavedLocation from '../../layout/PageNavSavedLocation';
import ButtonLinkLightBlue from '../../components/buttons/ButtonLinkLightBlue';
import { DirectionArrowSVG, getDirectionText } from '../../utils/helpers';
import {Helmet} from "react-helmet";

const SpotOverview = () => {

    const { id } = useParams();
    const location = useLocation();
    const { spots } = useSelector(state => state.savedSpots);
    const currentSpot = id ? spots[id] : null;
    const dispatch = useDispatch();

    React.useEffect(() => {
        dispatch(fetchSpot(id));
    }, [dispatch, id]);

    React.useEffect(() => {
        if (id) dispatch(setLastSpotRoute({ spotId: id, path: location.pathname }));
    }, [dispatch, id, location.pathname]);

    const renderOverview = () => {
        if(!currentSpot) {
            return <></>
        }

        const hasPreferences = currentSpot.preferred_wave_height_min || 
                              currentSpot.preferred_swell_period_min || 
                              currentSpot.preferred_swell_directions?.length || 
                              currentSpot.preferred_wind_speed_min || 
                              currentSpot.preferred_wind_directions?.length;

        return (
            <>
            <h2 className="text-xl font-semibold mb-6 text-white mt-10">Overview</h2>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5'>

                <div>
                    <p className="text-sm text-custom-light-grey">Name</p>
                    <p className="text-lg font-medium text-white">{currentSpot.name}</p>
                </div>

                <div>
                    <p className="text-sm text-custom-light-grey">Coordinates</p>
                    <p className="text-lg font-medium text-white">{Number(currentSpot.latitude).toFixed(2)}, {Number(currentSpot.longitude).toFixed(2)}</p>
                </div>

                {!hasPreferences ? (
                    <div className="p-6 border-2 border-dashed border-custom-charcoal rounded-lg text-center">
                        <p className="text-custom-light-grey mb-4">No ideal conditions set yet</p>
                        <ButtonLinkLightBlue 
                            href={`/location/${currentSpot.id}/preferences`} 
                            text="Set Ideal Conditions" 
                        />
                    </div>
                ) : (
                    <>
                        <div>
                            <p className="text-sm text-custom-light-grey">Ideal Wave Height</p>
                            <p className="text-lg font-medium text-white">
                                {currentSpot.preferred_wave_height_min || '—'}m - {currentSpot.preferred_wave_height_max || '—'}m
                            </p>
                        </div>

                        <div>
                            <p className="text-sm text-custom-light-grey">Ideal Wave Period</p>
                            <p className="text-lg font-medium text-white">
                                {currentSpot.preferred_swell_period_min || '—'}s - {currentSpot.preferred_swell_period_max || '—'}s
                            </p>
                        </div>

                        <div>
                            <p className="text-sm text-custom-light-grey mb-2">Ideal Swell Direction</p>
                            <div className="flex items-center gap-3 flex-wrap">
                                {currentSpot.preferred_swell_directions?.length ? (
                                    currentSpot.preferred_swell_directions.map((degrees) => (
                                        <div key={degrees} className="flex items-center gap-1">
                                            <DirectionArrowSVG degrees={degrees} size={24} color="#13a4ec" />
                                            <p className="text-sm font-medium text-white">{getDirectionText(degrees)}</p>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-lg font-medium text-white/50">—</p>
                                )}
                            </div>
                        </div>

                        <div>
                            <p className="text-sm text-custom-light-grey">Ideal Wind Speed</p>
                            <p className="text-lg font-medium text-white">
                                {currentSpot.preferred_wind_speed_min || '—'}km/h - {currentSpot.preferred_wind_speed_max || '—'}km/h
                            </p>
                        </div>

                        <div>
                            <p className="text-sm text-custom-light-grey mb-2">Ideal Wind Direction</p>
                            <div className="flex items-center gap-3 flex-wrap">
                                {currentSpot.preferred_wind_directions?.length ? (
                                    currentSpot.preferred_wind_directions.map((degrees) => (
                                        <div key={degrees} className="flex items-center gap-1">
                                            <DirectionArrowSVG degrees={degrees} size={24} color="#fed900" />
                                            <p className="text-sm font-medium text-white">{getDirectionText(degrees)}</p>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-lg font-medium text-white/50">—</p>
                                )}
                            </div>
                        </div>

                        <div>
                            <p className="text-sm text-custom-light-grey mb-2">Ideal Sports</p>
                            <div className="flex items-center gap-2 flex-wrap">
                                {currentSpot.preferred_sport && currentSpot.preferred_sport.trim() !== '' ? (
                                    currentSpot.preferred_sport.split(',').map((sport, index) => (
                                        <span 
                                            key={index}
                                            className="px-3 py-1 bg-custom-charcoal border border-custom-light-blue text-custom-light-blue rounded text-sm font-medium"
                                        >
                                            {sport.trim()}
                                        </span>
                                    ))
                                ) : (
                                    <p className="text-lg font-medium text-white/50">—</p>
                                )}
                            </div>
                        </div>
                    </>
                )}

                
            </div>
            </>
        )
    }

    return(
        <>
            <Helmet>
                <title>Spot Overview | Oreca Pro</title>
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <Header />
            <MobileNav />

            <div className='pt-[76px] pb-[90px] px-4 max-w-[1100px] mx-auto'>
                <h1 className='text-2xl font-semibold mb-1 mt-2'>{currentSpot?.name ? currentSpot.name : 'Current Spot'}</h1>
                {currentSpot && (
                    <p className='text-xs text-custom-light-grey'>
                        {Number(currentSpot.latitude).toFixed(2)}, {Number(currentSpot.longitude).toFixed(2)}
                    </p>
                )}
                
                <PageNavSavedLocation id={currentSpot?.id} />

                {renderOverview()}

                
            </div>
        </>
    )
}

export default SpotOverview
